"use client";

import { useState, useEffect, useCallback } from "react";

export interface UseMediaQueryOptions {
  defaultValue?: boolean;
  ssr?: boolean;
}

export interface UseMediaQueryReturn {
  matches: boolean;
  media: string;
  addListener: (callback: (matches: boolean) => void) => () => void;
  removeListener: (callback: (matches: boolean) => void) => void;
}

/**
 * Хук для работы с медиа-запросами
 * @param query - CSS медиа-запрос
 * @param options - опции хука
 * @returns объект с результатом медиа-запроса и методами управления
 */
export function useMediaQuery(
  query: string,
  options: UseMediaQueryOptions = {},
): UseMediaQueryReturn {
  const { defaultValue = false, ssr = true } = options;

  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") {
      return ssr ? defaultValue : false;
    }
    return window.matchMedia(query).matches;
  });

  const [mediaQuery, setMediaQuery] = useState<MediaQueryList | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    setMediaQuery(mediaQueryList);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Устанавливаем начальное значение
    setMatches(mediaQueryList.matches);

    // Добавляем слушатель
    mediaQueryList.addEventListener("change", handleChange);

    return () => {
      mediaQueryList.removeEventListener("change", handleChange);
    };
  }, [query]);

  const addListener = useCallback(
    (callback: (matches: boolean) => void) => {
      if (!mediaQuery) {
        return () => {};
      }

      const handler = (event: MediaQueryListEvent) => {
        callback(event.matches);
      };

      mediaQuery.addEventListener("change", handler);

      return () => {
        mediaQuery.removeEventListener("change", handler);
      };
    },
    [mediaQuery],
  );

  const removeListener = useCallback(
    (callback: (matches: boolean) => void) => {
      if (!mediaQuery) {
        return;
      }

      const handler = (event: MediaQueryListEvent) => {
        callback(event.matches);
      };

      mediaQuery.removeEventListener("change", handler);
    },
    [mediaQuery],
  );

  return {
    matches,
    media: query,
    addListener,
    removeListener,
  };
}

/**
 * Хук для проверки размера экрана
 * @param breakpoint - точка останова
 * @returns объект с результатом медиа-запроса
 */
export function useBreakpoint(
  breakpoint: "sm" | "md" | "lg" | "xl" | "2xl",
): UseMediaQueryReturn {
  const breakpoints = {
    sm: "(min-width: 640px)",
    md: "(min-width: 768px)",
    lg: "(min-width: 1024px)",
    xl: "(min-width: 1280px)",
    "2xl": "(min-width: 1536px)",
  };

  return useMediaQuery(breakpoints[breakpoint]);
}

const MOBILE_BREAKPOINT_PX = 768;

/**
 * Хук для проверки мобильного экрана (max-width: 767px)
 * @returns true, если ширина экрана меньше MOBILE_BREAKPOINT_PX
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;
}

// KAN-1004: отсюда удалены восемь обёрток над useMediaQuery — ориентация,
// hover, pointer, тёмная тема, reduced-motion, контраст, набор запросов и
// размер экрана. Ни одну не звали. Понадобится новая — это одна строка
// поверх useMediaQuery; мёртвый набор «на будущее» читается как API,
// который кто-то поддерживает.
